<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div class="text-center">
        <h2 class="text-3xl font-bold text-gray-900">Skapa konto</h2>
        <p class="mt-2 text-sm text-gray-600">
          Redan har ett konto?
          <router-link to="/login" class="font-medium text-indigo-600 hover:text-indigo-500">
            Logga in här
          </router-link>
        </p>
      </div>

      <form @submit.prevent="register" class="mt-8 space-y-6">
        <!-- Role Display (fixed from homepage selection) -->
        <div>
          <span class="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1.5 text-sm font-medium text-indigo-700">
            Kontotyp: {{ form.role === 'organizer' ? 'Eventorganisatör' : 'Biljettköpare' }}
          </span>
          <p class="mt-1 text-sm text-gray-500">
            {{ form.role === 'organizer' 
              ? 'Som organisatör kan du skapa evenemang och få din egen sida.' 
              : 'Som biljettköpare kan du köpa biljetter till evenemang.' 
            }}
          </p>
        </div>

        <!-- Organisatörsnamn (valfritt) -->
        <div>
          <label for="organizerName" class="block text-sm font-medium text-gray-700 mb-2">
            Organisatörsnamn (valfritt)
          </label>
          <input
            id="organizerName"
            v-model="form.organizerName"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Fyll i om du vill kunna skapa evenemang senare"
          />
          <p class="mt-1 text-sm text-gray-500">
            {{ form.role === 'organizer' 
              ? 'Detta namn kommer att visas på din organisatörssida.' 
              : 'Om du fyller i detta kan du enkelt växla till organisatör senare.' 
            }}
          </p>
        </div>

        <!-- Name Field -->
        <div>
          <label for="name" class="block text-sm font-medium text-gray-700 mb-1">
            {{ form.role === 'organizer' ? 'Organisatörsnamn' : 'Namn' }} *
          </label>
          <input
            id="name"
            v-model="form.name"
            type="text"
            required
            :class="[
              'appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm',
              nameError ? 'border-red-300' : 'border-gray-300'
            ]"
            :placeholder="form.role === 'organizer' ? 'Organisatörsnamn' : 'Ditt fullständiga namn'"
            @blur="validateName"
          />
          <p v-if="nameError" class="mt-1 text-sm text-red-600">{{ nameError }}</p>
        </div>


        <!-- Email Field -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
            E-postadress *
          </label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            :class="[
              'appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm',
              emailError ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="din@email.se"
            @blur="validateEmail"
          />
          <p v-if="emailError" class="mt-1 text-sm text-red-600">{{ emailError }}</p>
        </div>

        <!-- Password Field -->
        <div>
          <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
            Lösenord *
          </label>
          <input
            id="password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            required
            :class="[
              'appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm',
              passwordError ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Minst 6 tecken"
            @blur="validatePassword"
          />
          <div class="flex items-center justify-between mt-1">
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="text-sm text-gray-500 hover:text-gray-700"
            >
              {{ showPassword ? 'Dölj' : 'Visa' }} lösenord
            </button>
            <div class="flex space-x-1">
              <div 
                v-for="(criterion, index) in passwordCriteria" 
                :key="index"
                :class="[
                  'w-2 h-2 rounded-full',
                  criterion.met ? 'bg-green-500' : 'bg-gray-300'
                ]"
                :title="criterion.text"
              ></div>
            </div>
          </div>
          <p v-if="passwordError" class="mt-1 text-sm text-red-600">{{ passwordError }}</p>
        </div>

        <!-- Confirm Password Field -->
        <div>
          <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">
            Bekräfta lösenord *
          </label>
          <input
            id="confirmPassword"
            v-model="form.confirmPassword"
            type="password"
            required
            :class="[
              'appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm',
              confirmPasswordError ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Upprepa lösenordet"
            @blur="validateConfirmPassword"
          />
          <p v-if="confirmPasswordError" class="mt-1 text-sm text-red-600">{{ confirmPasswordError }}</p>
        </div>

        <!-- Organizer URL Preview -->
        <div v-if="form.role === 'organizer' && form.name" class="bg-blue-50 p-3 rounded-md">
          <p class="text-sm text-blue-800">
            <strong>Din organisatörssida kommer att vara:</strong><br>
            <span class="font-mono text-blue-600">https://biljett-se.vercel.app/{{ generateSlug(form.name) }}</span>
          </p>
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            :disabled="loading || !isFormValid"
            :class="[
              'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
              loading || !isFormValid
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700'
            ]"
          >
            <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-3">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            </span>
            {{ loading ? 'Skapar konto...' : 'Skapa konto' }}
          </button>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="rounded-md bg-red-50 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <span class="text-red-400">⚠️</span>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-700">{{ errorMessage }}</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

const router = useRouter();

// Form data
function resolveRole() {
  const q = router.currentRoute.value.query.role;
  if (q === 'organizer' || q === 'buyer') {
    sessionStorage.setItem('selectedRole', q);
    return q;
  }
  const saved = sessionStorage.getItem('selectedRole');
  if (saved === 'organizer' || saved === 'buyer') return saved;
  return 'buyer';
}

const initialRole = resolveRole();
const form = ref({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: initialRole,
  organizerName: ''
});

// UI state
const loading = ref(false);
const showPassword = ref(false);
const errorMessage = ref('');

// Validation errors
const nameError = ref('');
const emailError = ref('');
const passwordError = ref('');
const confirmPasswordError = ref('');

// Password criteria
const passwordCriteria = ref([
  { text: 'Minst 6 tecken', met: false },
  { text: 'Innehåller bokstäver', met: false },
  { text: 'Innehåller siffror', met: false }
]);

// Generate URL-friendly slug
const generateSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[åäö]/g, (match) => {
      const replacements = { 'å': 'a', 'ä': 'a', 'ö': 'o' };
      return replacements[match];
    })
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};


// Validation functions
const validateName = () => {
  if (!form.value.name.trim()) {
    nameError.value = 'Namn är obligatoriskt';
    return false;
  }
  if (form.value.name.trim().length < 2) {
    nameError.value = 'Namn måste vara minst 2 tecken';
    return false;
  }
  if (form.value.role === 'organizer' && form.value.name.trim().length < 3) {
    nameError.value = 'Organisatörsnamn måste vara minst 3 tecken';
    return false;
  }
  nameError.value = '';
  return true;
};

const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!form.value.email.trim()) {
    emailError.value = 'E-postadress är obligatorisk';
    return false;
  }
  if (!emailRegex.test(form.value.email)) {
    emailError.value = 'Ange en giltig e-postadress';
    return false;
  }
  emailError.value = '';
  return true;
};

const validatePassword = () => {
  const password = form.value.password;
  
  // Update password criteria
  passwordCriteria.value[0].met = password.length >= 6;
  passwordCriteria.value[1].met = /[a-zA-Z]/.test(password);
  passwordCriteria.value[2].met = /\d/.test(password);

  if (!password) {
    passwordError.value = 'Lösenord är obligatoriskt';
    return false;
  }
  if (password.length < 6) {
    passwordError.value = 'Lösenord måste vara minst 6 tecken';
    return false;
  }
  if (!/[a-zA-Z]/.test(password)) {
    passwordError.value = 'Lösenord måste innehålla bokstäver';
    return false;
  }
  if (!/\d/.test(password)) {
    passwordError.value = 'Lösenord måste innehålla siffror';
    return false;
  }
  passwordError.value = '';
  return true;
};

const validateConfirmPassword = () => {
  if (!form.value.confirmPassword) {
    confirmPasswordError.value = 'Bekräfta lösenord är obligatoriskt';
    return false;
  }
  if (form.value.password !== form.value.confirmPassword) {
    confirmPasswordError.value = 'Lösenorden matchar inte';
    return false;
  }
  confirmPasswordError.value = '';
  return true;
};

// Form validation
const isFormValid = computed(() => {
  return form.value.name && 
         form.value.email && 
         form.value.password && 
         form.value.confirmPassword &&
         !nameError.value &&
         !emailError.value &&
         !passwordError.value &&
         !confirmPasswordError.value;
});

// Watch for password changes to update criteria
watch(() => form.value.password, () => {
  validatePassword();
});

const register = async () => {
  // Validate all fields
  const isNameValid = validateName();
  const isEmailValid = validateEmail();
  const isPasswordValid = validatePassword();
  const isConfirmPasswordValid = validateConfirmPassword();

  if (!isNameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid) {
    return;
  }

  try {
    loading.value = true;
    errorMessage.value = '';

    const registrationData = {
      name: form.value.name.trim(),
      email: form.value.email.trim().toLowerCase(),
      password: form.value.password,
      role: form.value.role
    };

    // Add organizer-specific data
    if (form.value.role === 'organizer') {
      registrationData.organizerName = form.value.name.trim();
      registrationData.organizerSlug = generateSlug(form.value.name.trim());
      registrationData.isOrganizer = true;
    } else if (form.value.organizerName.trim()) {
      // If buyer but provided organizer name, save it for future use
      registrationData.organizerName = form.value.organizerName.trim();
      registrationData.organizerSlug = generateSlug(form.value.organizerName.trim());
    }

    const response = await axios.post('/api/auth/register', registrationData, {
      withCredentials: true
    });

    console.log('Registration successful:', response.data);
    
    // Show success message and redirect
    const successMessage = form.value.role === 'organizer' 
      ? 'Registrering lyckades! Du kan nu logga in och skapa evenemang.'
      : 'Registrering lyckades! Du kan nu logga in.';
    
    alert(successMessage);
    // Keep role when returning to login
    sessionStorage.setItem('selectedRole', form.value.role);
    router.push({ path: '/login', query: { role: form.value.role } });
    
  } catch (error) {
    console.error('Registration failed:', error);
    errorMessage.value = error.response?.data?.message || 'Ett fel uppstod vid registreringen. Försök igen.';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.input {
  display: block;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 0.375rem;
}
</style>
